import { IncidentsActivityPanelView } from '../view/IncidentsActivityPanelView';
import { templateIncidentsActivityPanel } from '../view/template';
import { IncidentsActivityPanelRepository } from '../repository/IncidentsActivityPanelRepository';
import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { IncidentsActivityPanelWebComponent } from '..';
import { ActivityEvents, GroupBy, IncidentsActivityPanelWebComponentSchema } from '../types';
import {
  alertsToTimelineItems,
  alertToTimelineItem,
  getTimeRangeConfig,
  mapAlertsByAlertName,
  mapAlertsByDate,
  mapAlertsByIncidentNumber,
  mapAlertsByKillchain,
  mapAlertsByNodes,
  mapAlertsBySensors,
  mapAlertsBySeverity,
} from '../utils';
import {
  AdditionalData,
  TimelineData,
  TimelineEvent,
  TimelineGroup,
  TimelineItem,
} from '../../../ui/gzCollapsibleTimeline/types';
import { AlertDetailsWebComponent } from '../../alertDetails';
import { html } from 'lit';
import {
  GzCollapsibleTimelineGroupWebComponent,
  GzCollapsibleTimelineItemWebComponent,
} from '../../../ui/gzCollapsibleTimeline';
import { InputEvents } from '../../../ui/gzInput/types';
import { elementClassListRemove, elementClassListToggle } from '../../../utils/htmlElement.utils';
import { TimeRangeEvents } from '../../../ui/gzTimeRangeSlider/types';
import { contentRenderer, timestampRenderer } from '../../../ui/gzCollapsibleTimeline/mock';

export class IncidentsActivityPanelController extends BaseWebComponentController {
  public component: IncidentsActivityPanelWebComponent;
  public dataRepository: IncidentsActivityPanelRepository;
  public view: IncidentsActivityPanelView;
  public viewConstructor: typeof IncidentsActivityPanelView;
  public dataRepositoryConstructor: typeof IncidentsActivityPanelRepository;

  constructor(component: IncidentsActivityPanelWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: IncidentsActivityPanelView,
      viewTemplate: templateIncidentsActivityPanel,
      dataRepositoryConstructor: IncidentsActivityPanelRepository,
    });
  }

  async getDataForView(): Promise<IncidentsActivityPanelWebComponentSchema> {
    return this.dataRepository.getDataForView();
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$singleSelectGroup,
        event: 'selectionChange',
        callback: this.changeSingleSelectGroup.bind(this),
      },
      {
        element: this.view.dom.$collapsibleTimeline,
        event: TimelineEvent.SLOT_HEADER_CLICK,
        callback: this.slotHeaderClick.bind(this),
      },
      {
        element: this.view.dom.$collapsibleTimeline,
        event: TimelineEvent.TOGGLE_PANEL,
        callback: this.togglePanel.bind(this),
      },
      {
        element: this.view.dom.$collapsibleTimeline,
        event: TimelineEvent.TOGGLE_ITEM_IN_GROUP,
        callback: this.toggleItemInGroup.bind(this),
      },
      {
        element: this.view.dom.$inputSearch,
        event: InputEvents.CHANGE,
        callback: this.handleSearch.bind(this),
      },
      {
        element: this.view.dom.$collapseBtn,
        event: 'click',
        callback: this.toggleCollapse.bind(this, true),
      },
      {
        element: this.view.dom.$uncollapsBtn,
        event: 'click',
        callback: this.toggleCollapse.bind(this, false),
      },
      {
        element: this.view.dom.$timeRangeSlider,
        event: TimeRangeEvents.TIME_RANGE_CHANGE,
        callback: this.handleTimeChange.bind(this),
      },
    ];
  }

  private toggleCollapse(collapsed: boolean) {
    this.component.dispatchEvent(
      new CustomEvent(ActivityEvents.COLLAPSE_PANEL, {
        detail: { collapsed },
        bubbles: true,
        composed: true,
      })
    );

    if (collapsed) {
      elementClassListToggle(this.view.dom.$wrapper, 'collapsed');
    } else {
      elementClassListRemove(this.view.dom.$wrapper, 'collapsed');
    }
  }

  private changeSingleSelectGroup(event: CustomEvent) {
    this.changeAlertsGroupBy(event.detail.selectedValue);
  }

  private changeAlertsGroupBy(groupBy) {
    this.view.dom.$inputSearch.value = '';

    const timeConfig = getTimeRangeConfig(this.dependencies.alerts);
    this.view.dom.$timeRangeSlider.valueMinTimestamp = timeConfig.minTimestamp;
    this.view.dom.$timeRangeSlider.valueMaxTimestamp = timeConfig.maxTimestamp;
    this.view.dom.$timeRangeSlider.calendarSelected = false;

    this.groupAlerts(groupBy, this.dependencies.alerts, true);
    this.component.dispatchEvent(new CustomEvent(ActivityEvents.CHANGE_GROUP_BY, { detail: { groupBy: groupBy } }));
  }

  public changeGroupBy(groupBy: GroupBy) {
    this.view.dom.$singleSelectGroup.setFormValue(groupBy);
    this.changeAlertsGroupBy(groupBy);
  }

  public removeHighlightTimeline() {
    this.view.dom.$collapsibleTimeline.resetTimeline();
  }

  private groupAlerts(groupBy, alerts, closeAlerts = false) {
    let groupedAlerts;
    let hasSuffixRenderer = false;
    switch (groupBy) {
      case GroupBy.KILLCHAIN_PHASE:
        groupedAlerts = mapAlertsByKillchain(alerts, this.dependencies.translateKillChainPhases);
        break;
      case GroupBy.SEVERITY:
        groupedAlerts = mapAlertsBySeverity(alerts, this.dependencies.translateEdrEventSeverity);
        break;
      case GroupBy.SENSOR:
        groupedAlerts = mapAlertsBySensors(alerts, this.dependencies.translateXdrSensors);
        break;
      case GroupBy.INCIDENT_ID:
        groupedAlerts = mapAlertsByIncidentNumber(alerts);
        break;
      case GroupBy.NODE:
        groupedAlerts = mapAlertsByNodes(alerts);
        break;
      case GroupBy.ALERT_NAME:
        groupedAlerts = mapAlertsByAlertName(alerts);
        break;
      default:
        groupedAlerts = mapAlertsByDate(alerts);
        hasSuffixRenderer = true;
    }

    this.view.dom.$collapsibleTimeline.initialData = this.handleGroupedAlerts(
      groupedAlerts,
      hasSuffixRenderer,
      groupBy
    );
    this.view.dom.$collapsibleTimeline.clearHighlight();
    this.view.dom.$collapsibleTimeline.resetScroll();

    if (closeAlerts) {
      this.view.dom.$collapsibleTimeline.clearOpenedPanel();
    }
  }

  private alertContentRenderer(alert, additionalData?: AdditionalData, alertGroup: any = null) {
    const alertDetails = new AlertDetailsWebComponent();
    alertDetails.alert = alert;
    alertDetails.group = additionalData?.ALERT_NAME_GROUP;
    alertDetails.translateKillChainPhases = this.dependencies.translateKillChainPhases;
    alertDetails.translateEdrEventSeverity = this.dependencies.translateEdrEventSeverity;
    alertDetails.translateXdrSensors = this.dependencies.translateXdrSensors;
    alertDetails.translateXdrGroupTitleByNodeType = this.dependencies.translateXdrGroupTitleByNodeType;
    alertDetails.translateXdrEntityType = this.dependencies.translateXdrEntityType;

    if (alertGroup) {
      alertDetails.alert = alertGroup;
    }

    alertDetails.addEventListener(ActivityEvents.VIEW_MORE_DETAILS, (event: CustomEvent) => {
      this.component.dispatchEvent(new CustomEvent(ActivityEvents.VIEW_MORE_DETAILS, { detail: event.detail }));
    });

    alertDetails.addEventListener(ActivityEvents.VIEW_NODE_DETAILS, (event: CustomEvent) => {
      this.component.dispatchEvent(new CustomEvent(ActivityEvents.VIEW_NODE_DETAILS, { detail: event.detail }));
    });

    return alertDetails;
  }

  private alertSuffixRenderer(alert) {
    const time = new Date(alert.lastUpdated).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    return html`
      <div style="display: flex; justify-content: center; align-items: center; margin-top: 5px">
        <gz-text fontSize="10">${time}</gz-text>
      </div>
    `;
  }

  private slotHeaderClick(event) {
    const timelineGroup = event.detail.group[0];
    const target = event.detail.target as HTMLElement;
    const isHighlight = target.hasAttribute('highlight');

    const alerts = this.dependencies.alerts.filter(alert =>
      timelineGroup.items.some(item => item.id.split('alert-id: ')[1] === alert._id)
    );
    this.component.dispatchEvent(
      new CustomEvent(TimelineEvent.SLOT_HEADER_CLICK, { detail: { alerts: alerts, highlight: isHighlight } })
    );
  }

  private togglePanel(event) {
    const collapsibleTimelineGroup = event.detail.target.querySelector(
      'gz-collapsible-timeline-group'
    ) as GzCollapsibleTimelineGroupWebComponent;
    const collapsibleItems = collapsibleTimelineGroup.shadowRoot?.querySelectorAll(
      'gz-collapsible-timeline-item'
    ) as NodeListOf<GzCollapsibleTimelineItemWebComponent>;

    collapsibleItems.forEach(item => {
      if (!event.detail.isOpen && item.isOpen) {
        item.removeAttribute('isOpen');
      }
    });

    if (!event.detail.isOpen) {
      const alerts = this.dependencies.alerts.filter(alert =>
        collapsibleTimelineGroup.items.some(item => item.id.split('alert-id: ')[1] === alert._id)
      );
      this.component.dispatchEvent(
        new CustomEvent(TimelineEvent.TOGGLE_PANEL, { detail: { alerts: alerts, highlight: false } })
      );
    }
  }

  private toggleItemInGroup(event) {
    const alertId = event.detail.id.split('alert-id: ')[1];
    const alerts = this.dependencies.alerts.filter(alert => alert._id === alertId);
    const groups = this.dependencies.alerts.filter(alertGroup =>
      alertGroup.alerts?.some(alert => alert._id === alertId)
    );
    this.component.dispatchEvent(
      new CustomEvent(TimelineEvent.TOGGLE_ITEM_IN_GROUP, {
        detail: { alerts: alerts.length > 0 ? alerts : groups, isOpen: event.detail.isOpen },
      })
    );
  }

  handleGroupedAlerts(groupedAlerts: Record<string, unknown[]>, hasSuffixRenderer, selectedValue) {
    if (selectedValue === GroupBy.ALERT_NAME) {
      const timelineItems: TimelineItem[] = [];
      // alert name is not grouped by category, instead it groups all single alerts
      for (const [key, alertEntry] of Object.entries(groupedAlerts)) {
        timelineItems.push(
          alertToTimelineItem(
            alertEntry[0],
            this.alertContentRenderer.bind(this),
            hasSuffixRenderer ? this.alertSuffixRenderer : undefined,
            key,
            alertEntry.length > 1 ? alertEntry.length : undefined,
            null,
            {
              ALERT_NAME_GROUP: alertEntry,
            }
          )
        );
      }
      return timelineItems;
    }
    let timelineData: (TimelineGroup | TimelineItem)[] = [];
    Object.entries(groupedAlerts).forEach(entry => {
      const [key, alerts] = entry;
      const items = alertsToTimelineItems(
        alerts,
        this.alertContentRenderer.bind(this),
        hasSuffixRenderer ? this.alertSuffixRenderer : undefined,
        key
      );
      timelineData.push({
        id: key,
        label: key,
        isCategory: true,
        items: items,
      });
      timelineData = [...timelineData, ...items];
    });
    return timelineData;
  }

  private handleSearch(event) {
    const filteredAlerts = this.applySearch(event.detail.sourceEvent, this.dependencies.alerts);
    this.applyTimeFilter(
      this.view.dom.$timeRangeSlider.valueMinTimestamp,
      this.view.dom.$timeRangeSlider.valueMaxTimestamp,
      filteredAlerts
    );
  }

  private applySearch(searchValue, alerts) {
    const filteredAlerts = alerts.filter(alert => {
      const alertName = alert.name ?? alert.alerts[0].name;
      return alertName.toLowerCase().includes(searchValue.toLowerCase());
    });

    const groupBy = this.view.dom.$singleSelectGroup.getFormValue();
    this.groupAlerts(groupBy, filteredAlerts, false);
    return filteredAlerts;
  }

  private applyTimeFilter(minTimestamp, maxTimestamp, alerts) {
    const filteredAlerts = alerts.filter(alert => {
      const alertTimestamp = alert.lastUpdated.getTime() / 1000;
      return alertTimestamp >= minTimestamp && alertTimestamp <= maxTimestamp;
    });

    const groupBy = this.view.dom.$singleSelectGroup.getFormValue();
    this.groupAlerts(groupBy, filteredAlerts, false);
    return filteredAlerts;
  }

  private handleTimeChange(event) {
    const filteredAlerts = this.applyTimeFilter(
      event.detail.valueMinTimestamp,
      event.detail.valueMaxTimestamp,
      this.dependencies.alerts
    );
    this.applySearch(this.view.dom.$inputSearch.value, filteredAlerts);
  }
}
